import { useEffect, useMemo, useRef, useState } from "react";
import { useAccount } from "wagmi";
import { type Address } from "viem";
import { CADENAS, COMISION_BPS, esEstable, esNativo } from "../lib/puente";
import { useTokens } from "../hooks/usePuente";
import { clave, useBarrido, useCarteras, useRefuel, REFUEL_USD, valorUsd, type Fila, type Moneda } from "../hooks/usePolvo";
import { Giro, Hueco } from "./Modal";
import { ModalOperacion } from "./ModalOperacion";
import { num } from "../lib/formato";
import { useT } from "../lib/i18n";

/**
 * BARRER EL POLVO. La otra mitad del puente: en vez de mover una cosa concreta, junta TODO lo que
 * hay suelto en las seis cadenas y lo convierte al estable que se elija.
 *
 * Lo que hace que esto sirva y no sea una lista mas:
 *
 *  · Se enseña TODO lo que tenga saldo, por minimo que sea — un resto de 0,004 tambien sale, que
 *    es justo lo que nadie mira. Quien no quiera verlo, tiene el filtro de «solo lo de menos de».
 *  · Se cotiza ANTES de firmar nada, y cada linea dice lo suyo: cuanto le queda, o por que no
 *    tiene ruta. La mayoria del polvo no llega al minimo del puente, y eso hay que decirlo aqui,
 *    no despues de que el usuario haya firmado.
 *  · El nativo de cada red se puede barrer, pero dejando el gas. Mandar el 100% del nativo es una
 *    transaccion que no se puede pagar a si misma.
 */
export function Polvo() {
  const t = useT();
  const { address } = useAccount();
  const [aCadena, setACadena] = useState(4663);
  const [aToken, setAToken] = useState<Address | "">("");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [tope, setTope] = useState("");

  const carteras = useCarteras();
  const tokensA = useTokens(aCadena);
  const barrido = useBarrido();
  const refuel = useRefuel();

  // La red que DONA el gas para un refuel: la que más nativo tiene, con margen de sobra sobre lo que
  // se trae (REFUEL_USD + un colchón para su propio gas y las comisiones del puente).
  const donanteGas = useMemo(() => {
    let mejor: Moneda | null = null;
    for (const r of carteras.redes) {
      const nat = r.monedas.find((m) => esNativo(m.address));
      if (nat && valorUsd(nat) >= REFUEL_USD + 1.5 && (!mejor || valorUsd(nat) > valorUsd(mejor))) mejor = nat;
    }
    return mejor;
  }, [carteras.redes]);
  // Una red está «sin gas» si tiene tokens con valor pero su nativo no llega ni a $0,50 (ni para una
  // transacción). Es cuando hace falta el refuel.
  const redSinGas = (monedas: Moneda[]) => {
    const nat = monedas.find((m) => esNativo(m.address));
    const tieneTokens = monedas.some((m) => !esNativo(m.address) && valorUsd(m) > 0);
    return tieneTokens && (nat ? valorUsd(nat) : 0) < 0.5;
  };

  const estables = useMemo(
    () => (tokensA.data ?? []).filter((t) => esEstable(t.symbol)),
    [tokensA.data]
  );
  useEffect(() => {
    if (estables.some((t) => t.address === aToken)) return;
    setAToken(estables[0]?.address ?? "");
  }, [estables, aToken]);

  const destino = useMemo(() => {
    const token = estables.find((t) => t.address === aToken);
    return token ? { cadena: aCadena, token } : null;
  }, [estables, aToken, aCadena]);

  // El destino no se barre a si mismo: convertir el USDG de Robinhood a USDG de Robinhood no es
  // nada, pero cotiza, cobra comision y da error raro.
  const candidatas = useMemo(
    () => carteras.monedas.filter(
      (m) => !(destino && m.cadena === destino.cadena && m.address.toLowerCase() === destino.token.address.toLowerCase())
    ),
    [carteras.monedas, destino]
  );

  const limite = parseFloat(tope.replace(",", ".")) || 0;
  const visibles = useMemo(
    () => (limite > 0 ? candidatas.filter((m) => valorUsd(m) <= limite) : candidatas),
    [candidatas, limite]
  );

  // Al terminar de leer las carteras se marcan solas las que casi siempre se quieren: todo menos
  // el nativo de cada red, que es el gas y se marca a mano. Solo la PRIMERA vez de cada lectura;
  // despues manda el usuario, que si desmarca algo no puede volver a aparecer marcado.
  const firma = candidatas.map((m) => clave(m.cadena, m.address)).join("|");
  const firmaAnterior = useRef("");
  useEffect(() => {
    if (!firma || firma === firmaAnterior.current) return;
    firmaAnterior.current = firma;
    setSel(new Set(candidatas.filter((m) => !esNativo(m.address)).map((m) => clave(m.cadena, m.address))));
  }, [firma, candidatas]);

  const marcadas = useMemo(
    () => candidatas.filter((m) => sel.has(clave(m.cadena, m.address))),
    [candidatas, sel]
  );
  const trabajando = !!barrido.ocupado;

  function alternar(m: Moneda) {
    const k = clave(m.cadena, m.address);
    setSel((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  }
  const marcarVisibles = (si: boolean) =>
    setSel((s) => {
      const n = new Set(s);
      for (const m of visibles) si ? n.add(clave(m.cadena, m.address)) : n.delete(clave(m.cadena, m.address));
      return n;
    });

  // Cuentas de la cabecera. `recibes` va en el token de destino (un estable, o sea ~dolares) y la
  // comision se pasa a dolares con el precio del token de ORIGEN, que es sobre el que se cobra.
  const cuentas = useMemo(() => {
    let recibes = 0, comision = 0, firmas = 0, conRuta = 0;
    for (const m of marcadas) {
      const f = barrido.filas[clave(m.cadena, m.address)];
      if (!f?.q) continue;
      conRuta++;
      recibes += f.q.recibesMin;
      comision += f.q.comision * (parseFloat(m.priceUSD ?? "0") || 0);
      firmas += f.q.pasos.length + (f.q.direccionAprobacion ? 1 : 0);
    }
    return { recibes, comision, firmas, conRuta };
  }, [marcadas, barrido.filas]);

  const valorMarcado = marcadas.reduce((s, m) => s + valorUsd(m), 0);
  const hechas = Object.entries(barrido.filas).filter(([, f]) => f.fase === "hecha");
  // Las que no tienen ruta se quedan fuera del barrido: firmarlas es tirar gas a sabiendas.
  const aBarrer = marcadas.filter((m) => barrido.filas[clave(m.cadena, m.address)]?.fase !== "sinruta");

  if (!address) return <p className="detalle">{t("Conecta tu wallet para ver lo que tienes suelto.")}</p>;

  return (
    <div className="panel polvo">
      {/* ---------------------------------------------------------------- destino */}
      <div className="dos">
        <label className="campo">
          <span>{t("Convertirlo todo a")}</span>
          <select value={aToken} onChange={(e) => setAToken(e.target.value as Address)}
            disabled={trabajando || !estables.length}>
            {!estables.length && <option value="">{tokensA.isPending ? t("cargando…") : t("sin estables aquí")}</option>}
            {estables.map((tk) => <option key={tk.address} value={tk.address}>{tk.symbol}</option>)}
          </select>
        </label>
        <label className="campo">
          <span>{t("En la red")}</span>
          <select value={aCadena} onChange={(e) => { setACadena(Number(e.target.value)); setAToken(""); }}
            disabled={trabajando}>
            {CADENAS.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </label>
      </div>

      {/* ---------------------------------------------------------------- controles */}
      <div className="polvo-mando">
        <div className="cuenta">
          <strong>{candidatas.length}</strong> {t("monedas con saldo")}
          {carteras.cargando && <> · <Giro texto={t("mirando las demás redes…")} /></>}
          {!carteras.cargando && <> · <span className="cifra">${num(carteras.total, 2)}</span> {t("en total")}</>}
        </div>
        <div className="acciones">
          <button className="chip" onClick={() => marcarVisibles(true)} disabled={trabajando}>{t("Todas")}</button>
          <button className="chip" onClick={() => marcarVisibles(false)} disabled={trabajando}>{t("Ninguna")}</button>
          <label className="filtro">
            {t("solo menos de $")}
            <input type="number" min="0" step="any" value={tope} placeholder={t("todo")}
              onChange={(e) => setTope(e.target.value)} disabled={trabajando} />
          </label>
          <button className="chip" onClick={() => carteras.refrescar()} disabled={trabajando}>{t("Releer")}</button>
        </div>
      </div>

      {/* ---------------------------------------------------------------- la lista */}
      <div className="polvo-lista">
        {carteras.redes.map((r) => {
          const suyas = visibles.filter((m) => m.cadena === r.id);
          if (!suyas.length && !r.cargando && !r.fallo) return null;
          return (
            <section key={r.id}>
              <header>
                <h3>{r.nombre}</h3>
                {r.cargando ? <Giro texto={t("leyendo…")} />
                  : r.fallo ? <span className="mal">{t("no contesta su RPC")}</span>
                  : <span className="cifra">${num(suyas.reduce((s, m) => s + valorUsd(m), 0), 2)}</span>}
              </header>
              {!r.cargando && !r.fallo && redSinGas(r.monedas) && (
                donanteGas && donanteGas.cadena !== r.id ? (
                  <div className="polvo-singas">
                    <span>{t("Sin {sim} para el gas — no puedes mover estos tokens hasta tener un poco.", { sim: r.moneda.symbol })}</span>
                    <button className="btn btn-sm" disabled={!!refuel.estado || trabajando}
                      onClick={() => refuel.traerGas(donanteGas, r.id)}>
                      {t("Traer ~${usd} de gas desde {red}", { usd: REFUEL_USD, red: CADENAS.find((c) => c.id === donanteGas.cadena)?.nombre ?? "" })}
                    </button>
                  </div>
                ) : (
                  <div className="polvo-singas sin-fuente">
                    <span>{t("Sin {sim} para el gas, y no tienes gas en otra red para traer. Manda un poco de {sim} a esta red desde un exchange.", { sim: r.moneda.symbol })}</span>
                  </div>
                )
              )}
              {r.cargando && <Hueco alto={34} n={2} />}
              {!r.cargando && !suyas.length && !r.fallo && (
                <p className="detalle">{limite > 0 ? t("Nada suelto aquí por debajo de ese importe.") : t("Nada suelto aquí.")}</p>
              )}
              <ul>
                {suyas.map((m) => (
                  <LineaMoneda key={clave(m.cadena, m.address)} moneda={m} destino={destino?.token.symbol}
                    marcada={sel.has(clave(m.cadena, m.address))} fila={barrido.filas[clave(m.cadena, m.address)]}
                    bloqueada={trabajando} alMarcar={() => alternar(m)} />
                ))}
              </ul>
            </section>
          );
        })}
        {!carteras.cargando && !candidatas.length && (
          <p className="detalle">{t("No tienes nada suelto en ninguna de las redes. Nada que barrer.")}</p>
        )}
      </div>

      {/* ---------------------------------------------------------------- resumen y boton */}
      <div className="polvo-pie">
        <div className="resumen-crear" style={{ borderTop: 0, paddingTop: 0 }}>
          <div>
            <span className="k">{t("Marcadas")}</span>
            <span className="v">{marcadas.length} · ${num(valorMarcado, 2)}</span>
          </div>
          {cuentas.conRuta > 0 && (
            <>
              <div>
                <span className="k">{t("Recibes al menos")}</span>
                <span className="v grande">{num(cuentas.recibes, 4)} {destino?.token.symbol}</span>
              </div>
              <div>
                <span className="k">{t("Comisión de Keellabs")}</span>
                <span className="v">≈ ${num(cuentas.comision, 4)} · {COMISION_BPS / 100}%</span>
              </div>
              <div><span className="k">{t("Firmas")}</span><span className="v">{cuentas.firmas}</span></div>
            </>
          )}
          {barrido.ocupado && (
            <div>
              <span className="k">
                <Giro texto={barrido.ocupado === "cotizando" ? t("buscando rutas…") : t("barriendo…")} />
              </span>
              <span className="v">{barrido.progreso.hechas} / {barrido.progreso.total}</span>
            </div>
          )}
        </div>

        {barrido.resumen && (
          <div className={`aviso ${barrido.resumen.fallos ? "error" : ""}`}>
            <p className="detalle">
              {barrido.resumen.hechas > 0
                ? t("Barridas {n} monedas a {token}. ", { n: barrido.resumen.hechas, token: destino?.token.symbol ?? "" })
                : t("No se barrió ninguna. ")}
              {barrido.resumen.fallos > 0 && t("{n} fallaron, cada una dice por qué. ", { n: barrido.resumen.fallos })}
              {barrido.resumen.parado && t("Lo paramos ahí.")}
            </p>
            {/* Las barridas ya no tienen saldo y desaparecen de la lista de arriba: sus recibos
                se quedan aqui, que es lo unico que queda para comprobar que salio. */}
            {!!hechas.length && (
              <ul className="polvo-hechas">
                {hechas.map(([k, f]) => {
                  const exp = CADENAS.find((c) => c.id === f.cadena)?.explorador;
                  return (
                    <li key={k}>
                      <span className="sim">{f.sim}</span>
                      {f.hash && exp
                        ? <a target="_blank" rel="noreferrer" href={`${exp}/tx/${f.hash}`}>{t("ver ↗")}</a>
                        : <span className="tenue">{t("hecho")}</span>}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <div className="polvo-botones">
          <button className="btn" disabled={!destino || trabajando || !marcadas.length}
            onClick={() => destino && barrido.cotizar(marcadas, destino)}>
            {barrido.ocupado === "cotizando" ? <Giro texto={t("Calculando…")} /> : t("Calcular lo que recibo")}
          </button>
          {barrido.ocupado === "barriendo" ? (
            <button className="btn" onClick={barrido.parar}>{t("Parar al acabar esta")}</button>
          ) : (
            <button className="btn principal" disabled={!destino || trabajando || !aBarrer.length}
              onClick={() => destino && barrido.barrer(aBarrer, destino)}>
              {aBarrer.length === 1
                ? t("Barrer {n} moneda a {token}", { n: aBarrer.length, token: destino?.token.symbol ?? "…" })
                : t("Barrer {n} monedas a {token}", { n: aBarrer.length, token: destino?.token.symbol ?? "…" })}
            </button>
          )}
        </div>
        <p className="detalle">
          {t("Va una por una y cada una pide su firma: puedes parar cuando quieras y lo ya barrido se queda hecho. Keellabs cobra el {pct}% de cada conversión, igual que en el puente.", { pct: COMISION_BPS / 100 })}
        </p>
      </div>
      <ModalOperacion estado={refuel.estado} cerrar={refuel.cerrar} />
    </div>
  );
}

/** Una moneda de la lista. El estado de su conversion va a la derecha, en la misma linea: dos
 *  columnas y no un desplegable, porque lo que se hace aqui es comparar veinte lineas de un vistazo. */
function LineaMoneda({
  moneda, marcada, fila, bloqueada, destino, alMarcar,
}: {
  moneda: Moneda;
  marcada: boolean;
  fila?: Fila;
  bloqueada: boolean;
  destino?: string;
  alMarcar: () => void;
}) {
  const t = useT();
  const usd = valorUsd(moneda);
  const nativo = esNativo(moneda.address);
  const explorador = CADENAS.find((c) => c.id === moneda.cadena)?.explorador;

  return (
    <li className={fila?.fase === "hecha" ? "hecha" : ""}>
      <label>
        <input type="checkbox" checked={marcada} onChange={alMarcar} disabled={bloqueada} />
        <span className="sim">{moneda.symbol}</span>
        {nativo && <span className="opcional" title={t("Es el gas de esta red: se barre dejando lo justo para pagar el envío")}>{t("gas")}</span>}
        <span className="cant">{num(moneda.saldo ?? 0, 8)}</span>
        <span className="usd">{usd > 0 ? `$${num(usd, 2)}` : "—"}</span>
      </label>
      <span className="estado">
        {fila?.fase === "cotizando" && <Giro texto={t("buscando ruta…")} />}
        {fila?.fase === "enviando" && <Giro texto={fila.nota ?? t("enviando…")} />}
        {fila?.fase === "lista" && fila.q && (
          <span className="bien">→ {num(fila.q.recibesMin, 4)} {destino}</span>
        )}
        {fila?.fase === "sinruta" && <span className="tenue">{fila.error}</span>}
        {fila?.fase === "fallo" && <span className="mal">{fila.error}</span>}
        {fila?.fase === "hecha" && (
          fila.hash && explorador
            ? <a className="bien" target="_blank" rel="noreferrer" href={`${explorador}/tx/${fila.hash}`}>{t("hecho ✓ ↗")}</a>
            : <span className="bien">{t("hecho ✓")}</span>
        )}
      </span>
    </li>
  );
}
